import { useLingui } from '@lingui/react';
import { Delta } from 'quill';
import React, { useState, FC, ReactNode, useMemo, createRef, useCallback, useEffect, useContext } from 'react';
import { isMobile } from 'react-device-detect';
import ReactQuill from 'react-quill';
import { useDebouncedCallback } from 'use-debounce';
import { useModalState } from '../../../Molecules/Dialogs';
import { FocusedNodeContext } from '../../../Service/Context/FocusedNode';
import parseDelta from '../../../Service/Placeholder/DeltaParser';
import { getAllPredefinedPlaceholders, fetchCustomPlaceholders } from '../../../Service/Placeholder/PlaceholderLabels';
import {
  replaceTemplatePlaceholdersWithHtml,
  getPlaceholderValue,
  PlaceholderDataSource,
} from '../../../Service/Placeholder/PlaceholderResover';
import { ToolbarControl } from '../../Rte/components/Toolbar/controls';

type Props = {
  children: ReactNode[];
  value?: string;
  lastModified?: Date;
  onInputChange?: (change: string) => void;
  finish: (change: string) => void;
  rest: PlaceholderDataSource;
};

export interface RteInputContextInterface {
  ref: React.RefObject<ReactQuill>;
  controls: ToolbarControl[][];
  mentions: Record<string, any>;
  displayValue: string;
  show: boolean;
  onDisplayValueChanged: (change: string) => void;
  onEmojiClick: (event: any, emojiObject: any) => void;
  onFocusCallback: (e: any) => void;
  onBlurCallback: (e: any) => void;
  toggle: () => void;
}

export const RteInputContext = React.createContext<RteInputContextInterface>({} as RteInputContextInterface);

export const RteInputContextProvider: FC<Props> = ({ value, lastModified, onInputChange, finish, rest, children }) => {
  const ref = createRef<ReactQuill>();

  const { i18n } = useLingui();

  const [displayValue, setDisplayValue] = useState('');

  const [lastChange, setLastChange] = useState<string | undefined>();

  const { setFocusedNode } = useContext<any>(FocusedNodeContext);

  const { show, toggle } = useModalState(false);

  const getUpdate = useCallback(() => {
    const content = ref.current?.editor?.getContents();

    if (!content) {
      return null;
    }

    const change = parseDelta(content as Delta);

    if (value === change) {
      return null;
    }

    return change;
  }, [ref, value]);

  const debouncedFinish = useDebouncedCallback(
    // function
    (value) => {
      if (value) {
        finish(value);
      }
    },
    // delay in ms
    1000,
  );

  const onDisplayValueChanged = useCallback(
    (val: string) => {
      setDisplayValue(val);
      const change = getUpdate();
      if (change !== null) {
        setLastChange(change);
        onInputChange?.(change);
        debouncedFinish(change);
      }
    },
    [debouncedFinish, getUpdate, onInputChange],
  );

  const { contract, customAttributeValues, customer, _document, event, eventLocation } = rest;

  useEffect(() => {
    setDisplayValue(
      replaceTemplatePlaceholdersWithHtml(i18n, value ?? '', {
        contract,
        customAttributeValues,
        customer,
        _document,
        event,
        eventLocation,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastModified, contract, customAttributeValues, customer, _document, eventLocation, event]);

  const getPredefinedPlaceholders = useCallback(
    () => getAllPredefinedPlaceholders({ contract, customAttributeValues, customer, _document, eventLocation, event }),
    [contract, customAttributeValues, customer, _document, eventLocation, event],
  );

  const resolvePlaceholderValueCallback = useCallback(
    (id: string) =>
      getPlaceholderValue(id, i18n, { contract, customAttributeValues, customer, _document, eventLocation, event }),
    [i18n, contract, customAttributeValues, customer, _document, eventLocation, event],
  );

  const matchSearchCallback = useCallback(
    (text: string, search: string) => i18n._(text).toLowerCase().includes(search),
    [i18n],
  );

  const mentions = useMemo(
    () => ({
      positioningStrategy: 'fixed',
      allowedChars: /^[\w:-]*$/,
      mentionDenotationChars: ['#'],
      showDenotationChar: false,
      source: async (searchTerm: string, renderList: (items: any[]) => void) => {
        const search = searchTerm.toLowerCase();

        const placeholderGroups = [...getPredefinedPlaceholders(), ...(await fetchCustomPlaceholders())];

        const includesSearchTerm = placeholderGroups
          .flatMap((group) => group.items)
          .filter((item) => matchSearchCallback(item.text, search))
          .map((item) => ({ id: item.placeholder, value: i18n._(item.text) }));
        renderList(includesSearchTerm);
      },
      onSelect: (item: any, insertItem: (data: any, programmaticInsert: boolean) => void) => {
        const resolvedValue = resolvePlaceholderValueCallback(item.id);
        insertItem(
          {
            id: item.id,
            value: resolvedValue !== null ? resolvedValue : item.value,
            denotationChar: resolvedValue !== null ? '' : '#',
            resolved: resolvedValue !== null,
          },
          false,
        );
      },
    }),
    [getPredefinedPlaceholders, i18n, matchSearchCallback, resolvePlaceholderValueCallback],
  );

  const onEmojiClick = useCallback(
    (event: any, emojiObject: any) => {
      const match = displayValue.match(/<\/\w+>$/);

      if (!match || match.length < 1) return;

      const lastIndex = displayValue.lastIndexOf(match[match.length - 1]);

      const before = displayValue.slice(0, lastIndex);

      const after = displayValue.slice(lastIndex);

      setDisplayValue(before + emojiObject.emoji + after);

      toggle();
    },
    [displayValue, toggle],
  );

  const onFocusCallback = useCallback(
    (e: any) => {
      setFocusedNode(e.target);
    },
    [setFocusedNode],
  );

  const onBlurCallback = useCallback((e: any) => {
    // if (value) {
    //   finish(value);
    // }
  }, []);

  const controls = useMemo<ToolbarControl[][]>(
    () =>
      isMobile
        ? [
            ['bold', 'italic', 'underline', 'link', 'clean'],
            ['portalLink'],
            // ['unorderedList', 'h1', 'h2', 'h3'],
            // ['alignLeft', 'alignCenter', 'alignRight'],
          ]
        : [
            ['bold', 'italic', 'underline', 'link', 'clean'],
            ['emoji'],
            ['portalLink'],
            // ['unorderedList', 'h1', 'h2', 'h3'],
            // ['alignLeft', 'alignCenter', 'alignRight'],
          ],
    [],
  );

  const providerModule = useMemo(
    () => ({
      ref,
      controls,
      mentions,
      displayValue,
      lastChange,
      show,
      onEmojiClick,
      onBlurCallback,
      onFocusCallback,
      onDisplayValueChanged,
      toggle,
    }),
    [
      ref,
      controls,
      mentions,
      displayValue,
      lastChange,
      show,
      onEmojiClick,
      onBlurCallback,
      onFocusCallback,
      onDisplayValueChanged,
      toggle,
    ],
  );

  return <RteInputContext.Provider value={providerModule}>{children}</RteInputContext.Provider>;
};
